import { periodePrecedente, type AccountKind, type Etat, type Regle, type ReferentielsDTO, type TypeInterventionDTO } from '@its/shared';

/**
 * LE MONDE FICTIF du mode démo (DEC-025) : un petit service d'assistance
 * complet — clients, contacts, demandes, interventions, un mois à facturer —,
 * gardé dans le navigateur (localStorage) et jamais envoyé à l'API.
 *
 * POURQUOI PAS UN BAC À SABLE EN BASE, comme Plans d'actions ? La borne écrit
 * un journal d'audit en ajout seul, des régularisations par déclencheur, et des
 * courriels qui partent pour de vrai ; ses tables métier sont dans le schéma
 * `borne`, partagé avec la Borne Commerciale. Une démonstration côté serveur y
 * laisserait des traces, et y toucherait des tables qui ne sont pas à nous.
 *
 * LES PERSONNES SONT INVENTÉES — exception écrite à la règle 10, limitée au
 * mode démo (DEC-025). Leurs adresses sont sous le domaine réservé `.invalid`
 * (RFC 2606) : aucune ne peut recevoir de courriel, et rien de réel ne
 * s'affiche à côté de données inventées.
 *
 * Les dates sont RELATIVES au jour : la démo ne vieillit pas. Le mois à
 * facturer est toujours le mois précédent, comme dans l'outil.
 */

// 3 : prestations Ekivox par client (DEC-027) au lieu des missions. Un monde plus ancien est recréé.
// 4 : usage interne / client des types d'intervention, et leur archivage (DEC-033).
// 6 : un client sans mission Ekivox, les demandes de mission et l'attente (DEC-043).
// 7 : des notes de facturation sur le mois à facturer (export des notes).
// 8 : une intervention garde le CODE de sa prestation, plus son identifiant (DEC-053).
export const VERSION_MONDE = 8;

/* ---------------------------------------------------------------- Personnages */

export const PERSONAS = ['demandeur', 'intervenant', 'facturation', 'administrateur'] as const;
export type PersonaId = (typeof PERSONAS)[number];

export const ID = {
  sophie: 'd0000000-0000-4000-8000-000000000001',
  thomas: 'd0000000-0000-4000-8000-000000000002',
  nadia: 'd0000000-0000-4000-8000-000000000003',
  julien: 'd0000000-0000-4000-8000-000000000004',
  lea: 'd0000000-0000-4000-8000-000000000005',
  marc: 'd0000000-0000-4000-8000-000000000006',
} as const;

export const COMPTE_DE_PERSONA: Record<PersonaId, string> = {
  demandeur: ID.sophie,
  intervenant: ID.thomas,
  facturation: ID.nadia,
  administrateur: ID.julien,
};

export const PERSONA_LIBELLES: Record<PersonaId, { nom: string; role: string; resume: string }> = {
  demandeur: { nom: 'Sophie MARCHAND', role: 'Cliente', resume: 'dépose et suit les demandes de son cabinet' },
  intervenant: { nom: 'Thomas LEROUX', role: 'Intervenant', resume: 'traite la file, saisit son temps' },
  facturation: { nom: 'Nadia BERGER', role: 'Facturation', resume: 'prépare le mois, envoie les relevés, alimente Ekivox' },
  administrateur: { nom: 'Julien FAURE', role: 'Administrateur', resume: 'gère les comptes et les rôles, traite aussi la file' },
};

/** Le domaine du « groupe » fictif : ses adresses sont celles du staff (connexion Microsoft). */
export const DOMAINE_GROUPE = 'its-demo.invalid';

/* ---------------------------------------------------------------- Types */

export interface CompteDemo {
  id: string;
  email: string;
  displayName: string;
  kind: AccountKind;
  /** Rôles DONNÉS ; les rôles effectifs se calculent (effectiveRoles), comme dans l'API. */
  roles: string[];
  active: boolean;
  lastLoginAt: string | null;
  /** Matricule de l'agent Ekivox fictif (DEC-039) ; absent s'il n'en a pas. */
  matricule?: string;
}

export interface ClientDemo {
  id: string;
  numero: string;
  nom: string;
  formeJuridique: string | null;
  interne: boolean;
  email: string | null;
  telephone: string | null;
  /** Typologie ITS du dossier Ekivox fictif (DEC-039). */
  clientIts: boolean;
}

export interface ContactDemo {
  id: string;
  clientId: string;
  nom: string;
  email: string | null;
  telephone?: string | null;
  /** Compte d'accès à la borne (contacts clients) : null tant qu'aucun accès n'a été ouvert. */
  compteId: string | null;
}

export interface DemandeDemo {
  id: string;
  numero: string;
  objet: string;
  description: string;
  typeId: number;
  urgenceId: number;
  etat: Etat;
  clientId: string;
  contactId: string;
  auteurId: string;
  intervenantId: string | null;
  dateDemande: string;
  dateCloture: string | null;
  canal: string;
}

export interface FilDemo {
  id: string;
  demandeId: string;
  date: string;
  sorte: 'message' | 'etat';
  auteurId: string | null;
  texte: string | null;
  interne: boolean;
  etatDe: Etat | null;
  etatVers: Etat | null;
  motif: string | null;
}

export interface MaterielDemo {
  libelle: string;
  destinataire: string | null;
  quantite: number;
}

export type TypeInterventionDemo = TypeInterventionDTO & { actif: boolean };

/** Le numéro d'IT Solutions dans le monde fictif : son temps est interne, jamais facturé (DEC-033). */
export const NUMERO_ITS_DEMO = 'DEMO-ITS';

/**
 * Les projets de Project'IT du monde fictif (0103), par code dossier du client
 * (`null` : projet interne sans client). Fixes : Project'IT n'existe pas en démo,
 * la borne ne fait que les lire. La migration du Cabinet Marchand porte sa
 * prestation Ekivox (0114) — le logiciel de la mission en cours —, le
 * renouvellement du parc pas encore : on y voit les deux cas. Tout le casting
 * en est membre : la démo n'a pas d'équipes de projet à tenir.
 */
export const PROJETS_IT_DEMO: {
  id: string;
  nom: string;
  nature: 'interne' | 'mission_client';
  clientNumero: string | null;
  missionEkivox: string | null;
  codeEkivox: string | null;
}[] = [
  { id: '0e0e0e0e-0000-4000-8000-000000000001', nom: 'Migration Microsoft 365', nature: 'mission_client', clientNumero: 'DEMO-0001', missionEkivox: 'M-DEMO-101', codeEkivox: 'LOGI0118' },
  { id: '0e0e0e0e-0000-4000-8000-000000000002', nom: 'Renouvellement du parc', nature: 'mission_client', clientNumero: 'DEMO-0002', missionEkivox: null, codeEkivox: null },
  { id: '0e0e0e0e-0000-4000-8000-000000000003', nom: 'Refonte de la supervision', nature: 'interne', clientNumero: 'DEMO-ITS', missionEkivox: null, codeEkivox: null },
  { id: '0e0e0e0e-0000-4000-8000-000000000004', nom: 'Veille sécurité', nature: 'interne', clientNumero: null, missionEkivox: null, codeEkivox: null },
];

export interface InterventionDemo {
  id: string;
  date: string;
  minutes: number;
  minutesNf: number;
  typeId: number;
  description: string;
  note: string | null;
  /** CODE de la prestation Ekivox imputée (DEC-053) : avec le dossier du client, la clé ; la mission se cherche à l'export. */
  prestation: string | null;
  /** La mission reçue à l'export : une contre-passation l'annule elle (0115). */
  exporteMission?: string | null;
  clientId: string | null;
  demandeId: string | null;
  intervenantId: string;
  presents: string[];
  materiel: MaterielDemo[];
  exporteLe: string | null;
  /** Projet Project'IT (0103) ; absent sur un monde sauvegardé avant lui. */
  projetItId?: string | null;
  /** Depuis quand elle attend sa mission (DEC-043) ; en attente seulement tant qu'elle n'a pas de prestation. */
  attenteMission?: string | null;
}

/** Une demande de mission AIDE0305, AIDE0306 pour le groupe (DEC-043, DEC-054). En démo, aucun courriel ne part. */
export interface DemandeMissionDemo {
  clientId: string;
  /** Absent d'un état de démo enregistré avant DEC-054 : c'était AIDE0305. */
  code?: string;
  le: string;
  parId: string;
}

export interface PieceJointeDemo {
  id: string;
  demandeId: string;
  nom: string;
  taille: number;
  interne: boolean;
  date: string;
  auteurId: string;
}

export interface PeriodiciteDemo {
  id: string;
  objet: string;
  description: string;
  clientId: string;
  contactId: string;
  regle: Regle;
  prochaineEcheance: string;
  actif: boolean;
  derniereGeneration: string | null;
}

export interface ReleveDemo {
  id: string;
  clientId: string;
  periode: string;
  version: number;
  destinataires: string[];
  nbInterventions: number;
  totalMinutes: number;
  envoyeLe: string;
  envoyePar: string;
}

/**
 * Une correction d'une ligne figée (règle 15) : ce que la base écrit par
 * déclencheur dans l'outil, le serveur de démo l'écrit lui-même.
 */
export interface RegularisationDemo {
  id: string;
  interventionId: string;
  clientId: string;
  periodeOrigine: string;
  operation: 'creation' | 'modification' | 'suppression';
  date: string;
  minutesAvant: number | null;
  minutesApres: number | null;
  description: string;
  motif: string;
  le: string;
  par: string;
  /** Relevé qui l'a portée ; null tant qu'elle attend le relevé suivant. */
  releveId: string | null;
  /** La ligne était partie vers Ekivox : contre-passation à mettre dans le prochain fichier. */
  ekivox: { avant: InterventionDemo | null; apres: InterventionDemo | null; exporteLe: string | null } | null;
}

/** Une prestation du miroir Ekivox fictif, avec sa mission (projet) et son client (code dossier). */
export interface PrestationDemo {
  id: string;
  code: string;
  libelle: string;
  /** Statut de la prestation : A_FAIRE, EN_COURS, TERMINEE… */
  statut: string;
  projetId: string;
  projet: string;
  /** Statut de la mission : A_FAIRE, EN_COURS, FAIT, ANNULE. */
  projetStatut: string;
  dateDebut: string | null;
  dateFin: string | null;
  numeroDossier: string;
  supprimee: boolean;
  /** Société Ekivox de la mission ; absente : IT Solutions (04). */
  societe?: string;
}

export interface Monde {
  version: number;
  compteur: number;
  comptes: CompteDemo[];
  clients: ClientDemo[];
  contacts: ContactDemo[];
  /** Les types d'intervention gardent leurs archivés : l'historique en affiche le libellé (DEC-033). */
  referentiels: Omit<ReferentielsDTO, 'typesIntervention'> & { typesIntervention: TypeInterventionDemo[] };
  demandes: DemandeDemo[];
  fil: FilDemo[];
  interventions: InterventionDemo[];
  piecesJointes: PieceJointeDemo[];
  periodicites: PeriodiciteDemo[];
  /** Le miroir Ekivox du monde fictif : les prestations, par code dossier client (DEC-027). */
  prestations: PrestationDemo[];
  releves: ReleveDemo[];
  regularisations: RegularisationDemo[];
  demandesMission: DemandeMissionDemo[];
  /** Dernière ouverture d'une demande par son preneur en charge, « compte:demande » → date (0096). */
  lectures?: Record<string, string>;
}

/* ---------------------------------------------------------------- Dates relatives */

const MINUTE = 60_000;
const HEURE = 60 * MINUTE;
const JOUR = 24 * HEURE;

/** Il y a `jours` jours, à `h:m` heure locale. */
function ilYa(jours: number, h = 9, m = 0): string {
  const d = new Date(Date.now() - jours * JOUR);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

/** Un jour du mois précédent (borné aux jours ouvrés du mois), à `h:m`. */
function moisPrecedent(jour: number, h = 9, m = 0): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, jour, h, m);
  // Un samedi ou un dimanche glisse au lundi : un relevé ne compte pas de week-end.
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function aujourdhuiIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Le mois à facturer : toujours le mois précédent, comme l'écran de pré-facturation. */
export function moisAFacturer(): string {
  return periodePrecedente(aujourdhuiIso());
}

/* ---------------------------------------------------------------- Monde de départ */

const uuid = (n: number): string => `d0000000-0000-4000-8000-${String(n).padStart(12, '0')}`;

export function mondeDeDepart(): Monde {
  const annee = new Date().getFullYear();
  const CLIENT = { marchand: uuid(101), garage: uuid(102), pharmacie: uuid(103), its: uuid(104), boulangerie: uuid(105) };
  const CONTACT = {
    sophie: uuid(201),
    paul: uuid(202),
    marc: uuid(203),
    claire: uuid(204),
    accueil: uuid(205),
    thomas: uuid(206),
    nadia: uuid(207),
    julien: uuid(208),
    lea: uuid(209),
  };
  // Référentiels : les mêmes libellés que l'outil, pour que la démo se lise comme lui.
  const referentiels: Monde['referentiels'] = {
    types: [
      { id: 1, code: 'incident', libelle: 'Incident' },
      { id: 2, code: 'demande', libelle: 'Demande de service' },
      { id: 3, code: 'materiel', libelle: 'Matériel' },
      { id: 4, code: 'acces', libelle: 'Accès et comptes' },
    ],
    urgences: [
      { id: 1, code: 'faible', libelle: 'Faible' },
      { id: 2, code: 'modere', libelle: 'Modérée' },
      { id: 3, code: 'bloquant', libelle: 'Bloquante' },
    ],
    typesIntervention: [
      { id: 11, code: 'distance', libelle: 'Assistance à distance', interne: false, client: true, actif: true },
      { id: 12, code: 'sur_site', libelle: 'Intervention sur site', interne: false, client: true, actif: true },
      { id: 13, code: 'administration', libelle: 'Administration d’infrastructure', interne: true, client: true, actif: true },
      { id: 14, code: 'reunion', libelle: 'Réunion d’équipe', interne: true, client: false, actif: true },
      { id: 15, code: 'gestion_prospect', libelle: 'Gestion de prospect', interne: true, client: true, actif: true },
    ],
  };

  const comptes: CompteDemo[] = [
    { id: ID.sophie, email: 'sophie.marchand@cabinet-marchand.invalid', displayName: 'Sophie MARCHAND', kind: 'client', roles: [], active: true, lastLoginAt: ilYa(1, 8, 40) },
    { id: ID.thomas, email: `thomas.leroux@${DOMAINE_GROUPE}`, displayName: 'Thomas LEROUX', kind: 'staff', roles: ['intervenant'], active: true, lastLoginAt: ilYa(0, 8, 5), matricule: 'TLERO' },
    { id: ID.nadia, email: `nadia.berger@${DOMAINE_GROUPE}`, displayName: 'Nadia BERGER', kind: 'staff', roles: ['facturation'], active: true, lastLoginAt: ilYa(2, 9, 12), matricule: 'NBERG' },
    { id: ID.julien, email: `julien.faure@${DOMAINE_GROUPE}`, displayName: 'Julien FAURE', kind: 'staff', roles: ['administrateur', 'intervenant'], active: true, lastLoginAt: ilYa(0, 7, 55), matricule: 'JFAUR' },
    { id: ID.lea, email: `lea.roussel@${DOMAINE_GROUPE}`, displayName: 'Léa ROUSSEL', kind: 'staff', roles: ['intervenant'], active: true, lastLoginAt: ilYa(0, 8, 30) },
    { id: ID.marc, email: 'marc.durand@garage-durand.invalid', displayName: 'Marc DURAND', kind: 'client', roles: [], active: true, lastLoginAt: ilYa(6, 17, 20) },
  ];

  const clients: ClientDemo[] = [
    { id: CLIENT.marchand, numero: 'DEMO-0001', nom: 'Cabinet Marchand & Associés', formeJuridique: 'SELARL', interne: false, email: 'accueil@cabinet-marchand.invalid', telephone: '02 00 00 00 01', clientIts: true },
    { id: CLIENT.garage, numero: 'DEMO-0002', nom: 'Garage Durand', formeJuridique: 'SARL', interne: false, email: 'contact@garage-durand.invalid', telephone: '02 00 00 00 02', clientIts: true },
    { id: CLIENT.pharmacie, numero: 'DEMO-0003', nom: 'Pharmacie des Tilleuls', formeJuridique: 'SNC', interne: false, email: null, telephone: '02 00 00 00 03', clientIts: false },
    // Aucune prestation dans le miroir fictif : la mission manque, on la demande (DEC-043).
    { id: CLIENT.boulangerie, numero: 'DEMO-0004', nom: 'Boulangerie Lemoine', formeJuridique: 'EURL', interne: false, email: null, telephone: '02 00 00 00 04', clientIts: false },
    { id: CLIENT.its, numero: 'DEMO-ITS', nom: 'IT Solutions (démo)', formeJuridique: 'SAS', interne: true, email: `support@${DOMAINE_GROUPE}`, telephone: null, clientIts: false },
  ];

  const contacts: ContactDemo[] = [
    { id: CONTACT.sophie, clientId: CLIENT.marchand, nom: 'Sophie MARCHAND', email: 'sophie.marchand@cabinet-marchand.invalid', telephone: '06 00 00 00 11', compteId: ID.sophie },
    { id: CONTACT.paul, clientId: CLIENT.marchand, nom: 'Paul VIDAL', email: 'paul.vidal@cabinet-marchand.invalid', compteId: null },
    { id: CONTACT.marc, clientId: CLIENT.garage, nom: 'Marc DURAND', email: 'marc.durand@garage-durand.invalid', compteId: ID.marc },
    { id: CONTACT.claire, clientId: CLIENT.pharmacie, nom: 'Claire ROBIN', email: 'claire.robin@pharmacie-tilleuls.invalid', compteId: null },
    { id: CONTACT.accueil, clientId: CLIENT.pharmacie, nom: 'Accueil de la pharmacie', email: null, compteId: null },
    { id: CONTACT.thomas, clientId: CLIENT.its, nom: 'Thomas LEROUX', email: `thomas.leroux@${DOMAINE_GROUPE}`, compteId: ID.thomas },
    { id: CONTACT.nadia, clientId: CLIENT.its, nom: 'Nadia BERGER', email: `nadia.berger@${DOMAINE_GROUPE}`, compteId: ID.nadia },
    { id: CONTACT.julien, clientId: CLIENT.its, nom: 'Julien FAURE', email: `julien.faure@${DOMAINE_GROUPE}`, compteId: ID.julien },
    { id: CONTACT.lea, clientId: CLIENT.its, nom: 'Léa ROUSSEL', email: `lea.roussel@${DOMAINE_GROUPE}`, compteId: ID.lea },
  ];

  const D = (n: number) => uuid(300 + n);
  const num = (n: number) => `BT-${annee}-${String(n).padStart(5, '0')}`;
  const demandes: DemandeDemo[] = [
    {
      id: D(1), numero: num(1), objet: 'Imprimante du 2e étage hors ligne', typeId: 3, urgenceId: 2, etat: 'emise',
      description: 'Depuis ce matin, l’imprimante du 2e étage ne répond plus. Le voyant réseau clignote orange et plus personne ne peut imprimer.',
      clientId: CLIENT.marchand, contactId: CONTACT.sophie, auteurId: ID.sophie, intervenantId: null, dateDemande: ilYa(0, 8, 47), dateCloture: null, canal: 'portail',
    },
    {
      id: D(2), numero: num(2), objet: 'Accès VPN refusé depuis la mise à jour', typeId: 4, urgenceId: 3, etat: 'attente_demandeur',
      description: 'Depuis la mise à jour de mon poste, le VPN refuse ma connexion avec « certificat invalide ». Je ne peux plus travailler à distance.',
      clientId: CLIENT.marchand, contactId: CONTACT.sophie, auteurId: ID.sophie, intervenantId: ID.thomas, dateDemande: ilYa(2, 9, 15), dateCloture: null, canal: 'portail',
    },
    {
      id: D(3), numero: num(3), objet: 'Sauvegarde nocturne en échec', typeId: 1, urgenceId: 3, etat: 'prise_en_charge',
      description: 'Le rapport de sauvegarde de cette nuit indique un échec sur le serveur de fichiers.',
      clientId: CLIENT.garage, contactId: CONTACT.marc, auteurId: ID.marc, intervenantId: ID.thomas, dateDemande: ilYa(1, 7, 30), dateCloture: null, canal: 'portail',
    },
    {
      id: D(4), numero: num(4), objet: 'Nouveau poste pour l’accueil', typeId: 2, urgenceId: 1, etat: 'prise_en_charge',
      description: 'Une nouvelle préparatrice arrive lundi : il lui faut un poste, une session et l’accès au logiciel de la pharmacie.',
      clientId: CLIENT.pharmacie, contactId: CONTACT.claire, auteurId: ID.lea, intervenantId: ID.lea, dateDemande: ilYa(3, 10, 0), dateCloture: null, canal: 'pour_compte',
    },
    {
      id: D(5), numero: num(5), objet: 'Renouveler le certificat du portail client', typeId: 2, urgenceId: 2, etat: 'emise',
      description: 'Le certificat du portail client expire dans dix jours. À renouveler et à déployer sur le frontal.',
      clientId: CLIENT.its, contactId: CONTACT.julien, auteurId: ID.julien, intervenantId: null, dateDemande: ilYa(1, 16, 20), dateCloture: null, canal: 'portail',
    },
    {
      id: D(6), numero: num(6), objet: 'Messagerie très lente le matin', typeId: 1, urgenceId: 1, etat: 'cloturee',
      description: 'Entre 8 h et 9 h, l’ouverture de la messagerie prend plusieurs minutes.',
      clientId: CLIENT.marchand, contactId: CONTACT.sophie, auteurId: ID.sophie, intervenantId: ID.thomas, dateDemande: ilYa(6, 9, 5), dateCloture: ilYa(1, 15, 30), canal: 'portail',
    },
    {
      id: D(7), numero: num(7), objet: 'Remplacer le switch de l’atelier', typeId: 3, urgenceId: 2, etat: 'gelee',
      description: 'Le switch de l’atelier perd des ports un par un. Remplacement à prévoir.',
      clientId: CLIENT.garage, contactId: CONTACT.marc, auteurId: ID.marc, intervenantId: ID.lea, dateDemande: ilYa(9, 11, 0), dateCloture: null, canal: 'portail',
    },
  ];

  const F = (n: number) => uuid(400 + n);
  const fil: FilDemo[] = [
    { id: F(1), demandeId: D(2), date: ilYa(2, 9, 40), sorte: 'etat', auteurId: ID.thomas, texte: null, interne: false, etatDe: 'emise', etatVers: 'prise_en_charge', motif: null },
    { id: F(2), demandeId: D(2), date: ilYa(2, 10, 5), sorte: 'message', auteurId: ID.thomas, texte: 'Le certificat du poste a expiré à la mise à jour. Pouvez-vous me dire quand le poste est allumé et sur le réseau du cabinet ? J’interviendrai à distance.', interne: false, etatDe: null, etatVers: null, motif: null },
    { id: F(3), demandeId: D(2), date: ilYa(2, 10, 6), sorte: 'message', auteurId: ID.thomas, texte: 'Même souci probable sur les deux autres postes mis à jour hier : à vérifier quand on y sera.', interne: true, etatDe: null, etatVers: null, motif: null },
    { id: F(4), demandeId: D(2), date: ilYa(2, 10, 7), sorte: 'etat', auteurId: ID.thomas, texte: null, interne: false, etatDe: 'prise_en_charge', etatVers: 'attente_demandeur', motif: null },
    { id: F(5), demandeId: D(3), date: ilYa(1, 8, 0), sorte: 'etat', auteurId: ID.thomas, texte: null, interne: false, etatDe: 'emise', etatVers: 'prise_en_charge', motif: null },
    { id: F(6), demandeId: D(4), date: ilYa(3, 10, 0), sorte: 'etat', auteurId: ID.lea, texte: null, interne: false, etatDe: 'emise', etatVers: 'prise_en_charge', motif: null },
    { id: F(7), demandeId: D(6), date: ilYa(5, 9, 0), sorte: 'etat', auteurId: ID.thomas, texte: null, interne: false, etatDe: 'emise', etatVers: 'prise_en_charge', motif: null },
    { id: F(8), demandeId: D(6), date: ilYa(1, 15, 30), sorte: 'etat', auteurId: ID.thomas, texte: null, interne: false, etatDe: 'prise_en_charge', etatVers: 'cloturee', motif: null },
    { id: F(9), demandeId: D(7), date: ilYa(8, 14, 0), sorte: 'etat', auteurId: ID.lea, texte: null, interne: false, etatDe: 'emise', etatVers: 'prise_en_charge', motif: null },
    { id: F(10), demandeId: D(7), date: ilYa(8, 14, 30), sorte: 'etat', auteurId: ID.lea, texte: null, interne: false, etatDe: 'prise_en_charge', etatVers: 'gelee', motif: 'En attente de la livraison du switch commandé par le client (prévue sous 15 jours).' },
  ];

  const I = (n: number) => uuid(500 + n);
  const inter = (n: number, x: Omit<InterventionDemo, 'id' | 'presents' | 'materiel' | 'exporteLe' | 'note' | 'prestation' | 'minutesNf'> & Partial<InterventionDemo>): InterventionDemo => ({
    id: I(n), presents: [], materiel: [], exporteLe: null, note: null, prestation: null, minutesNf: 0, ...x,
  });
  const interventions: InterventionDemo[] = [
    // Sur les demandes ouvertes
    inter(1, { date: ilYa(2, 10, 0), minutes: 30, typeId: 11, description: 'Diagnostic à distance : certificat VPN expiré à la mise à jour.', clientId: CLIENT.marchand, demandeId: D(2), intervenantId: ID.thomas }),
    inter(2, { date: ilYa(1, 8, 15), minutes: 45, typeId: 13, description: 'Analyse des journaux de sauvegarde : disque cible plein.', clientId: CLIENT.garage, demandeId: D(3), intervenantId: ID.thomas, note: 'Proposer un disque de plus grande capacité.' }),
    inter(3, { date: ilYa(5, 9, 30), minutes: 45, typeId: 11, description: 'Reconstruction du profil de messagerie et compactage de la boîte.', clientId: CLIENT.marchand, demandeId: D(6), intervenantId: ID.thomas }),
    inter(4, { date: ilYa(1, 14, 0), minutes: 120, minutesNf: 30, typeId: 12, description: 'Préparation et installation du poste de l’accueil.', clientId: CLIENT.pharmacie, demandeId: D(4), intervenantId: ID.lea, presents: [ID.thomas], materiel: [{ libelle: 'Poste fixe 24"', destinataire: 'Accueil', quantite: 1 }] }),
    // Temps interne de la semaine
    inter(5, { date: ilYa(0, 8, 30), minutes: 45, typeId: 14, description: 'Point hebdomadaire de l’équipe.', clientId: null, demandeId: null, intervenantId: ID.thomas, presents: [ID.lea, ID.julien] }),
    // Le mois à facturer : des lignes prêtes, et du temps sans prestation
    inter(10, { date: moisPrecedent(4, 9, 0), minutes: 90, typeId: 12, description: 'Remplacement de l’onduleur de la baie.', clientId: CLIENT.marchand, demandeId: null, intervenantId: ID.thomas, prestation: 'AIDE0305', materiel: [{ libelle: 'Onduleur 1500 VA', destinataire: null, quantite: 1 }], note: 'Onduleur fourni par nos soins : à facturer avec la main-d’œuvre.' }),
    inter(11, { date: moisPrecedent(9, 14, 0), minutes: 60, typeId: 11, description: 'Mise à jour des postes et contrôle de l’antivirus.', clientId: CLIENT.marchand, demandeId: null, intervenantId: ID.thomas, prestation: 'AIDE0305' }),
    inter(12, { date: moisPrecedent(12, 10, 0), minutes: 45, typeId: 11, description: 'Ouverture d’une boîte aux lettres pour une nouvelle collaboratrice.', clientId: CLIENT.marchand, demandeId: null, intervenantId: ID.lea, prestation: 'AIDE0305' }),
    inter(13, { date: moisPrecedent(16, 9, 0), minutes: 180, minutesNf: 30, typeId: 12, description: 'Câblage et installation du nouveau switch de l’atelier.', clientId: CLIENT.garage, demandeId: null, intervenantId: ID.thomas, prestation: 'AIDE0305', presents: [ID.lea], note: 'Deux techniciens sur place : facturer le déplacement une seule fois.' }),
    inter(14, { date: moisPrecedent(18, 11, 0), minutes: 30, typeId: 13, description: 'Contrôle mensuel des sauvegardes.', clientId: CLIENT.garage, demandeId: null, intervenantId: ID.thomas, prestation: 'AIDE0305' }),
    inter(15, { date: moisPrecedent(22, 15, 0), minutes: 60, typeId: 11, description: 'Paramétrage de l’imprimante du comptoir.', clientId: CLIENT.pharmacie, demandeId: null, intervenantId: ID.thomas }),
    inter(16, { date: moisPrecedent(23, 10, 0), minutes: 15, typeId: 11, description: 'Réinitialisation d’un mot de passe.', clientId: CLIENT.pharmacie, demandeId: null, intervenantId: ID.lea }),
  ];

  const prochaine = (j: number) => {
    const d = new Date(Date.now() + j * JOUR);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return {
    version: VERSION_MONDE,
    compteur: 1000,
    comptes,
    clients,
    contacts,
    referentiels,
    demandes,
    fil,
    interventions,
    piecesJointes: [
      { id: uuid(601), demandeId: D(1), nom: 'photo-voyant-imprimante.jpg', taille: 182_400, interne: false, date: ilYa(0, 8, 48), auteurId: ID.sophie },
    ],
    periodicites: [
      {
        id: uuid(701), objet: 'Contrôle mensuel des sauvegardes', description: 'Vérifier le rapport de sauvegarde du mois et tester une restauration.',
        clientId: CLIENT.garage, contactId: CONTACT.marc, regle: { frequence: 'mensuelle', jourMois: 1 }, prochaineEcheance: prochaine(12), actif: true, derniereGeneration: ilYa(18, 6, 0),
      },
      {
        id: uuid(702), objet: 'Point hebdomadaire de supervision', description: 'Relire les alertes de supervision de la semaine.',
        clientId: CLIENT.its, contactId: CONTACT.julien, regle: { frequence: 'hebdomadaire', joursSemaine: [1] }, prochaineEcheance: prochaine(4), actif: false, derniereGeneration: null,
      },
    ],
    // Le miroir Ekivox du monde fictif, sur le modèle des vraies prestations IT Solutions : une au temps
    // passé (AIDE0305) et une mise à disposition de logiciel (LOGI…) par client. Une prestation d'une
    // mission close exprès (Marchand) : le sélecteur ne la propose plus, sauf sur une intervention qui la porte.
    // Le Cabinet Marchand a aussi sa lettre de mission comptable (société 02, DEC-052) : sa révision REVI0101
    // et le SAV des outils Altonéo se rangent sous « Mission EC ».
    prestations: [
      { id: 'S-DEMO-111', code: 'REVI0101', libelle: 'Révision des comptes annuels', statut: 'A_FAIRE', projetId: 'M-DEMO-111', projet: `L3V1_EC_RECURRENT_${annee}_01_DEMO-0001`, projetStatut: 'A_FAIRE', dateDebut: `${annee}-01-01`, dateFin: `${annee}-12-31`, numeroDossier: 'DEMO-0001', supprimee: false, societe: '02' },
      { id: 'S-DEMO-112', code: 'DIGT0301', libelle: 'SAV sur les outils Altonéo', statut: 'A_FAIRE', projetId: 'M-DEMO-111', projet: `L3V1_EC_RECURRENT_${annee}_01_DEMO-0001`, projetStatut: 'A_FAIRE', dateDebut: `${annee}-01-01`, dateFin: `${annee}-12-31`, numeroDossier: 'DEMO-0001', supprimee: false, societe: '02' },
      { id: 'S-DEMO-101', code: 'AIDE0305', libelle: 'Intervention de l’équipe IT Solutions', statut: 'EN_COURS', projetId: 'M-DEMO-101', projet: `DIGITAL_${annee}_DEMO-0001`, projetStatut: 'EN_COURS', dateDebut: `${annee}-01-01`, dateFin: `${annee}-12-31`, numeroDossier: 'DEMO-0001', supprimee: false },
      { id: 'S-DEMO-102', code: 'LOGI0118', libelle: 'Logiciel de gestion GEST DO IT Formule Avancée', statut: 'EN_COURS', projetId: 'M-DEMO-101', projet: `DIGITAL_${annee}_DEMO-0001`, projetStatut: 'EN_COURS', dateDebut: `${annee}-01-01`, dateFin: `${annee}-12-31`, numeroDossier: 'DEMO-0001', supprimee: false },
      { id: 'S-DEMO-103', code: 'AIDE0305', libelle: 'Intervention de l’équipe IT Solutions', statut: 'TERMINEE', projetId: 'M-DEMO-102', projet: `DIGITAL_${annee - 1}_DEMO-0001`, projetStatut: 'FAIT', dateDebut: `${annee - 1}-01-01`, dateFin: `${annee - 1}-12-31`, numeroDossier: 'DEMO-0001', supprimee: false },
      { id: 'S-DEMO-205', code: 'AIDE0305', libelle: 'Intervention de l’équipe IT Solutions', statut: 'EN_COURS', projetId: 'M-DEMO-205', projet: `DIGITAL_${annee}_DEMO-0002`, projetStatut: 'EN_COURS', dateDebut: `${annee - 1}-09-01`, dateFin: null, numeroDossier: 'DEMO-0002', supprimee: false },
      { id: 'S-DEMO-301', code: 'AIDE0305', libelle: 'Intervention de l’équipe IT Solutions', statut: 'A_FAIRE', projetId: 'M-DEMO-301', projet: `DIGITAL_${annee}_DEMO-0003`, projetStatut: 'A_FAIRE', dateDebut: `${annee}-01-01`, dateFin: `${annee}-12-31`, numeroDossier: 'DEMO-0003', supprimee: false },
    ],
    releves: [],
    regularisations: [],
    demandesMission: [],
  };
}

/* ---------------------------------------------------------------- Persistance */

const CLE = 'bt.demo.monde';

export function chargerMonde(): Monde {
  try {
    const brut = localStorage.getItem(CLE);
    if (brut) {
      const m = JSON.parse(brut) as Monde;
      if (m.version === VERSION_MONDE) return m;
    }
  } catch {
    /* stockage indisponible ou abîmé : on repart du monde de départ */
  }
  return mondeDeDepart();
}

export function sauverMonde(m: Monde): void {
  try {
    localStorage.setItem(CLE, JSON.stringify(m));
  } catch {
    /* la démo continue en mémoire */
  }
}

export function effacerMonde(): void {
  try {
    localStorage.removeItem(CLE);
  } catch {
    /* rien à effacer */
  }
}
